import React, { useCallback, useMemo, useState } from 'react';
import { TagIcon } from 'lucide-react';
import SaveToFavorites from '../UI/SaveToFavorites';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import GraphicDetailCard from '../Graphics/GraphicDetailCard';
export interface GalleryItem {
  id: string;
  imageUrl: string;
  title: string;
  creator: string;
  price: number;
  priceType?: 'fixed' | 'starting' | 'commissioned';
  serviceType?: string;
  genre?: string[];
  description?: string;
  aspectRatio?: 'portrait' | 'landscape' | 'square';
}
interface GalleryViewProps {
  items: GalleryItem[];
  title: string;
  categoryName: string;
}
const GalleryView: React.FC<GalleryViewProps> = ({
  items,
  title,
  categoryName
}) => {
  const dk = useDarkClasses();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  // Format price display
  const formatPrice = (item: GalleryItem) => {
    if (item.priceType === 'starting') {
      return `From $${item.price}`;
    } else if (item.priceType === 'commissioned') {
      return `Comm: $${item.price}`;
    } else {
      return `$${item.price}`;
    }
  };
  const openLightbox = (index: number) => setSelectedIndex(index);
  const closeLightbox = useCallback(() => setSelectedIndex(null), []);
  const prev = useCallback(() => {
    setSelectedIndex(i => i === null ? null : (i - 1 + items.length) % items.length);
  }, [items.length]);
  const next = useCallback(() => {
    setSelectedIndex(i => i === null ? null : (i + 1) % items.length);
  }, [items.length]);
  const selectedItem = selectedIndex !== null ? items[selectedIndex] : null;
  // Distribute items evenly across columns using round-robin
  // We create column sets for each breakpoint and use CSS to show the right one
  const distributeItems = (items: GalleryItem[], colCount: number) => {
    const columns: {
      item: GalleryItem;
      originalIndex: number;
    }[][] = Array.from({
      length: colCount
    }, () => []);
    items.forEach((item, index) => {
      columns[index % colCount].push({
        item,
        originalIndex: index
      });
    });
    return columns;
  };
  const cols2 = useMemo(() => distributeItems(items, 2), [items]);
  const cols3 = useMemo(() => distributeItems(items, 3), [items]);
  const cols4 = useMemo(() => distributeItems(items, 4), [items]);
  const cols5 = useMemo(() => distributeItems(items, 5), [items]);
  const renderColumn = (column: {
    item: GalleryItem;
    originalIndex: number;
  }[], colIndex: number) => <div key={colIndex} className="flex flex-col gap-4" data-id="element-1364" data-mp="1rj8j795q08qh">
      {column.map(({
      item,
      originalIndex
    }) => <div key={item.id} className={`relative rounded-lg shadow-md overflow-hidden group cursor-pointer ${dk.isDark ? 'bg-gray-800' : 'bg-white'}`} onClick={() => openLightbox(originalIndex)} data-id="element-1365" data-mp="1cqyac8aneukj">
          <div className="relative" data-id="element-1366" data-mp="18r69yiveruq9">
            <img src={item.imageUrl} alt={item.title} className="w-full h-auto object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-1367" data-mp="18q4odkgrnpua" />
            {/* Price Tag Overlay */}
            <div className="absolute bottom-3 right-3 bg-black/70 text-white px-2 py-1 rounded-md text-sm font-medium" data-id="element-1368" data-mp="y6ccjnqwvyyp">
              {formatPrice(item)}
            </div>
            {/* Save Button - visible on hover */}
            <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" data-id="element-1369" data-mp="183ysmdjy5wk9">
              <SaveToFavorites itemId={item.id} size="sm" data-id="element-1370" data-mp="134m8i0kgn3ld" />
            </div>
            {/* Hover Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4" data-id="element-1371" data-mp="1hts65k3nglr5">
              <h3 className="text-white font-semibold" data-id="element-1372" data-mp="1qvj7kcr5q6f7">{item.title}</h3>
              <p className="text-gray-200 text-sm" data-id="element-1373" data-mp="1lwojowec1ro8">by {item.creator}</p>
              {item.serviceType && <span className="mt-1 inline-block bg-indigo-600/80 text-white text-xs px-2 py-1 rounded" data-id="element-1374" data-mp="3gbc3k6tsivu6">
                  {item.serviceType}
                </span>}
            </div>
          </div>
        </div>)}
    </div>;
  const renderGrid = (columns: {
    item: GalleryItem;
    originalIndex: number;
  }[][]) => <>{columns.map((col, i) => renderColumn(col, i))}</>;
  return <div className="container mx-auto px-4 py-8" data-id="element-1375" data-mp="2xf3vywdb28vp">
      {title && <h1 className={`text-3xl font-bold mb-2 ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1376" data-mp="32ddpl2jdt1f7">
          {title}
        </h1>}
      <p className={`mb-8 ${dk.isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-1377" data-mp="36gabp384rfnw">
        Browse {categoryName} from our talented creative professionals
      </p>
      {/* Gallery Grid - Even Distribution Masonry */}
      {items.length > 0 ? <>
          {/* 2 columns: mobile to sm */}
          <div className="grid grid-cols-2 gap-4 md:hidden" data-id="element-1378" data-mp="qomh3egl9z89">
            {renderGrid(cols2)}
          </div>
          {/* 3 columns: md */}
          <div className="hidden md:grid md:grid-cols-3 lg:hidden gap-4" data-id="element-1379" data-mp="vsoowpsgw6e1">
            {renderGrid(cols3)}
          </div>
          {/* 4 columns: lg */}
          <div className="hidden lg:grid lg:grid-cols-4 xl:hidden gap-4" data-id="element-1380" data-mp="qykh64bdnubw">
            {renderGrid(cols4)}
          </div>
          {/* 5 columns: xl+ */}
          <div className="hidden xl:grid xl:grid-cols-5 gap-4" data-id="element-1381" data-mp="i11s8oum4vuq">
            {renderGrid(cols5)}
          </div>
        </> : <div className={`text-center py-12 rounded-lg ${dk.isDark ? 'bg-gray-800' : 'bg-gray-50'}`} data-id="element-1382" data-mp="d38o01cvebx3">
          <TagIcon className={`mx-auto h-12 w-12 ${dk.isDark ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-1383" data-mp="40hbdy89da3v" />
          <h3 className={`mt-2 text-lg font-medium ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1384" data-mp="22gdjc9rzdesa">
            No items found
          </h3>
          <p className={`mt-1 ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1385" data-mp="3rx7no0k8wcla">
            Try adjusting your filters or search term
          </p>
        </div>}
      {/* Detail Card Popup */}
      {selectedItem && <GraphicDetailCard imageUrl={selectedItem.imageUrl} title={selectedItem.title} creator={selectedItem.creator} serviceType={selectedItem.serviceType} description={selectedItem.description} genre={selectedItem.genre} price={selectedItem.price} priceType={selectedItem.priceType} onClose={closeLightbox} onPrev={items.length > 1 ? prev : undefined} onNext={items.length > 1 ? next : undefined} itemIndex={selectedIndex ?? 0} totalItems={items.length} data-id="element-1386" data-mp="3ixub2z0tr7wc" />}
    </div>;
};
export default GalleryView;